"use client";

import { queryClient } from "@/lib/queryClient";
import { createContext, useContext, useEffect, useState } from "react";
import { Socket } from "socket.io-client";
import { io } from "socket.io-client";
import { toast } from "sonner";

interface SocketContextType {
  socket: Socket | null;
  subscribe: (channel: string) => void;
  unsubscribe: (channel: string) => void;
  publish: (channel: string, message: any) => void;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  subscribe: () => {},
  unsubscribe: () => {},
  publish: () => {},
});

export const useSocket = () => useContext(SocketContext);

export function SocketProvider({ children }: { children: React.ReactNode }) {
  const [socket, setSocket] = useState<Socket | null>(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    const newSocket = io(
      process.env.NEXT_PUBLIC_WS_URL || "ws://localhost:8080",
      {
        auth: { token },
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      }
    );

    newSocket.on("connect", () => {
      console.log("Socket connected successfully");
      // Subscribe to both notifications and image channels
      newSocket.emit('subscribe', 'notifications');
      newSocket.emit('subscribe', 'images');
    });

    // Handle notifications
    newSocket.on("notifications", (data) => {
      if (data) {
        try {
          console.log("Notification received:", data);

          // Use `toast.success` or `toast.error` based on `data.result`
          if (data?.result === "success") {
            toast.success("1 new notification", {
              description: (
                <div>
                  <p>{data?.message || "Operation completed successfully."}</p>
                  {data?.url && (
                    <a
                      href={data.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:text-blue-700 underline mt-2 block"
                    >
                      Download
                    </a>
                  )}
                </div>
              ),
              
            });
            queryClient.invalidateQueries("categories" as any);
          } else {
            toast.error("1 new notification", {
              description: data?.message || "Operation completed successfully.",
              dismissible: true,
              closeButton: true
            });
          }
        } catch (error) {
          console.error("Error handling notification:", error);
        }
      }
    });

    newSocket.on("connect_error", (error) => {
      console.error("Socket connection error:", error.message);
      if (error.message === "Authentication error") {
        toast.error("Authentication error");
      }
    });

    newSocket.on("disconnect", (reason) => {
      console.log("Socket disconnected:", reason);
    });

    setSocket(newSocket);

    return () => {
      newSocket.off("notification");
      newSocket.off("images");
      newSocket.disconnect();
    };
  }, []);

  const subscribe = (channel: string) => {
    if (socket) {
      socket.emit("subscribe", channel);
    }
  };

  const unsubscribe = (channel: string) => {
    if (socket) {
      socket.emit("unsubscribe", channel);
    }
  };

  const publish = (channel: string, message: any) => {
    if (socket) {
      socket.emit("publish", { channel, message });
    }
  };

  return (
    <SocketContext.Provider value={{ socket, subscribe, unsubscribe, publish }}>
      {children}
    </SocketContext.Provider>
  );
}
